import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import sanitizeHtml from '../utils/sanitizeHtml';
import './PortraitDialogue.css';

/**
 * PortraitDialogue
 *
 * JRPG-style overlay shown during NPC conversations. Renders:
 *   - The NPC portrait on the left (sprite art from /images/sprites/)
 *   - The active player's portrait on the right (character_portraits/...)
 *   - A name plate identifying the active speaker
 *   - The most recent line(s) of dialogue
 *   - The available response choices (replaces the legacy choices overlay)
 *
 * Receives null/undefined for portrait fields gracefully — silhouettes used as
 * fallback so the layout never collapses mid-conversation.
 */
function Portrait({ portrait, side, fallback, speaking }) {
  const [src, setSrc] = useState(portrait ? portrait.url : null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrc(portrait ? portrait.url : null);
    setFailed(false);
  }, [portrait && portrait.url]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleError = () => {
    if (fallback && src !== fallback) {
      setSrc(fallback);
    } else {
      // Both primary and fallback failed — render silhouette
      setFailed(true);
    }
  };

  const showImage = src && !failed;

  return (
    <div className={`pd-portrait pd-portrait-${side} ${speaking ? 'pd-speaking' : 'pd-listening'}`}>
      {showImage ? (
        <img src={src} alt="" onError={handleError} draggable={false} />
      ) : (
        <div className="pd-portrait-silhouette" aria-hidden="true">?</div>
      )}
    </div>
  );
}

/**
 * Extract the most recent dialogue line(s) for display in the text panel.
 * The full transcript already lives in the GameLog scroll, so the panel only
 * shows the latest beat — by convention, every line after the last <br>.
 */
function extractLatestLine(rawHtml) {
  if (!rawHtml || typeof rawHtml !== 'string') return '';
  const parts = rawHtml.split(/<br\s*\/?>/i).map(s => s.trim()).filter(Boolean);
  if (parts.length === 0) return '';
  // Show up to the last 2 lines so a "Speaker: line / continuation" beat reads naturally
  const tail = parts.slice(-2).join('<br>');
  return tail;
}

const PortraitDialogue = ({
  context,
  choices,
  onSelect,
  onClose,
  characterPortrait
}) => {
  const panelRef = useRef(null);

  if (!context && (!choices || choices.length === 0)) return null;

  const speaker = (context && context.speaker) || (context && context.npcDisplayName) || '';
  const npcPortrait = (context && context.npcPortrait) || null;
  const playerPortrait = (context && context.playerPortrait) || characterPortrait || null;
  const latestText = extractLatestLine(context && context.text);
  const playerSpeakingTurn = !!(context && context.lastChoiceText);

  return createPortal(
    <div className="portrait-dialogue-overlay" ref={panelRef} role="dialog" aria-label="Conversation">
      <div className="portrait-dialogue-stage">
        <Portrait
          portrait={npcPortrait}
          side="left"
          speaking={!playerSpeakingTurn}
        />
        <Portrait
          portrait={playerPortrait}
          side="right"
          fallback={playerPortrait && playerPortrait.fallback}
          speaking={playerSpeakingTurn}
        />
      </div>

      <div className="portrait-dialogue-panel">
        {onClose && (
          <button
            type="button"
            className="pd-close-button"
            onClick={onClose}
            aria-label="Close conversation (Esc)"
            title="Close (Esc)"
          >
            ×
          </button>
        )}
        {speaker && (
          <div className="pd-nameplate">
            {speaker}
          </div>
        )}
        {latestText && (
          <div
            className="pd-text"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(latestText) }}
          />
        )}

        {choices && choices.length > 0 && (
          <div className="pd-choices">
            {choices.map((choice, index) => (
              <button
                key={index}
                type="button"
                className="pd-choice-button"
                onClick={() => onSelect && onSelect(index)}
              >
                {choice.meta && choice.meta.shiftsAlignment && (
                  <span
                    className="pd-pill pd-pill-align"
                    title="This choice will shift your alignment."
                  >⚖️</span>
                )}
                {choice.meta && choice.meta.locksChain && (
                  <span
                    className="pd-pill pd-pill-lock"
                    title="This choice locks out other quest paths."
                  >🔒</span>
                )}
                <span className="pd-choice-text">{choice.text}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PortraitDialogue;
